import React, { useState } from "react";
import './styles.css';

interface Column {
  id: string;
  type: string;
  label: string;
  format?: string;
  currency?: string;
  decimals?: number;
}

interface TableRow {
  id: string;
  nome: string;
  detalhes: {
    linhas: Array<Array<{
      key: string;
      label: string;
      type: string;
      value: any;
    }>>;
  };
}

interface TableData {
  columns: Column[];
  rows: Record<string, any>[];
  display_name?: string;
}

interface MultiTableRendererProps {
  tables: Array<Record<string, TableData>>;
}

const formatValue = (value: any, col: Column) => {
  if (value == null) return "-";

  switch (col.type) {
    
    case "double":
    case "integer":
    case "number":
      return new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: col.decimals ?? 0,
        maximumFractionDigits: col.decimals ?? 2,
      }).format(Number(value));

    case "currency":
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: col.currency ?? "BRL",
        minimumFractionDigits: col.decimals ?? 2,
        maximumFractionDigits: col.decimals ?? 2,
      }).format(Number(value));

    case "percent":
      return `${(Number(value) * 100).toFixed(col.decimals ?? 2)}%`;

    default:
      return String(value);
  }
};

const MultiTableRenderer: React.FC<MultiTableRendererProps> = ({ tables }) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (rowId: string) => {
    const newExpanded = new Set(expandedRows);
    newExpanded.has(rowId) ? newExpanded.delete(rowId) : newExpanded.add(rowId);
    setExpandedRows(newExpanded);
  };

  const toggleAllRows = () => {
    if (expandedRows.size === 0) {
      const allIds = tables.map((_, i) => i.toString());
      setExpandedRows(new Set(allIds));
    } else {
      setExpandedRows(new Set());
    }
  };

  const mainTableData = tables
  .map((tableObj, index): TableRow | null => {
    const [name, table] = Object.entries(tableObj)[0];
    const displayName =
      table && typeof table === "object" && "display_name" in (table as any)
        ? (table as any).display_name
        : name;

    if (displayName === 'Infos Cessão') {
      return null;
    }

    // Verificações de segurança para evitar erros de undefined
    if (!table || !table.rows || !Array.isArray(table.rows) || !table.columns || !Array.isArray(table.columns)) {
      return {
        id: index.toString(),
        nome: name || 'Tabela sem nome',
        detalhes: {
          linhas: [],
        },
      };
    }
    
    return {
      id: index.toString(),
      nome: displayName,
      detalhes: {
        linhas: table.rows.map((row) =>
          table.columns.map((col) => ({
            key: col.id,
            label: col.label,
            type: col.type,
            value: row[col.id],
          }))
        ),
      },
    };
  })
  .filter((row): row is TableRow => row !== null);

  const getColumnsFromData = (data: any[][]) => {
    if (data.length === 0) return [];
    return data[0].map((item) => ({
      key: item.key,
      label: item.label,
      type: item.type,
    }));
  };

  const HeaderData: React.FC<{ data: Array<Record<string, any>> }> = ({ data }) => {
    return (
      <div>
        {data.map((element, index) => {
          const key = Object.keys(element)[0];
          if (key === "zinfocessao") {
            const value = element[key];
            
            // Verificações de segurança para evitar erros de undefined
            if (!value || !value.rows || !Array.isArray(value.rows) || value.rows.length === 0) {
              return null;
            }
            
            const row = value.rows[0];
            
            // Verificação adicional para garantir que row existe
            if (!row) {
              return null;
            }

            return (
              <div key={index}>
                <div className="card-detalhe-contrato">
                  <div className="card-detalhe-contrato-header">
                    <div className="card-detalhe-contrato-header-title">Overview</div>
                  </div>
                  <div className="card-detalhe-contrato-body">
                    <div className="card-detalhe-contrato-body-content">
                      <div className="row">
                        {value.columns && Array.isArray(value.columns) && value.columns.map((col: any, idx: any) => (
                          <div key={idx} className="col-md-3">
                            <div className="card-detalhe-contrato-body-title">{col.label}</div>
                            <div className="card-detalhe-contrato-body-value">
                              {formatValue(row[col.id], col)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          return null;
        })}
      </div>
    );
  };




  return (
    <>
    <div className="header-container">
      <HeaderData data={tables}/>
    </div>

    <div className="table-section">
      <div className="table-header">
        <div className="table-title">
          <span className="table-subtitle">Indicadores</span>
        </div>
        <div className="table-controls">
          <button
            className="control-btn"
            title={expandedRows.size === 0 ? "Expandir todas" : "Fechar todas"}
            onClick={toggleAllRows}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="currentColor"
              className={`expand-arrow ${expandedRows.size > 0 ? "expanded" : ""}`}
            >
              <path d="M3 6h10l-5 5L3 6z" />
            </svg>
          </button>
        </div>
      </div>

      <div className="fiori-table-container">
        <table className="fiori-table">
          <tbody>
            {mainTableData.map((row) => (
              <React.Fragment key={row.id}>
                <tr
                  className={`fiori-tr ${expandedRows.has(row.id) ? "fiori-tr-expanded" : ""}`}
                  onClick={() => toggleRow(row.id)}
                >
                  <td className="fiori-td-expand">
                    <div
                      className={`fiori-expand-icon ${
                        expandedRows.has(row.id) ? "expanded" : ""
                      }`}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M6 4L10 8L6 12V4Z" />
                      </svg>
                    </div>
                  </td>
                  <td className="fiori-td-name">{row.nome}</td>
                </tr>

                {expandedRows.has(row.id) && (
                  <tr className="fiori-tr-detail-integrated">
                    <td colSpan={2} className="fiori-botd-detail-integrated">
                      <table className="fiori-detail-table">
                        <thead>
                          <tr>
                            {getColumnsFromData(row.detalhes.linhas).map((col, index) => (
                              <th key={index} className="fiori-detail-th">
                                {col.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {row.detalhes.linhas.map((linha, rowIndex) => (
                            <tr key={rowIndex} className="fiori-detail-tr">
                            {linha.map((item, colIndex) => (
                              <td key={colIndex} className="fiori-detail-td">
                                {formatValue(item.value, { id: item.key, type: item.type, label: item.label })}
                              </td>
                            ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </>
  );
};

export default MultiTableRenderer;
